import { ApiException, Operation } from './components/backend/backendapi';
import { WindowType } from './util';
import { xsrftMiddleware } from './xsrf';
import { Toasted } from 'vue-toasted';
import VueI18n from 'vue-i18n';

declare var window: WindowType;

export interface ClientMiddleware {
  onRequest?(url: RequestInfo, init: RequestInit): Promise<void> | void;

  onResponse?(res: Response): Promise<Response> | Response;
}

export function initClientWithMiddlewares<T>(
  clazz: { new(baseUrl: string, http: { fetch(url: RequestInfo, init?: RequestInit); }): T },
  middlewares: ClientMiddleware[]): T {
  let fetch = {
    fetch: async function (url: RequestInfo, init?: RequestInit): Promise<Response> {
      init ??= {};
      for (let middleware of middlewares) {
        await middleware.onRequest?.(url, init);
      }

      let res = await window.fetch(url, init);
      for (let middleware of middlewares) {
        if (middleware.onResponse) {
          res = await middleware.onResponse(res);
        }
      }
      return res;
    },
  };
  return new clazz(window.application.baseUrl.replace(/\/$/, ''), fetch);
}

export function initClient<T>(clazz: { new(baseUrl: string, http: { fetch(url: RequestInfo, init?: RequestInit); }): T }): T {
  return initClientWithMiddlewares<T>(clazz, [xsrftMiddleware(), tokenExpiredMiddleware(), apiKeyMiddleware()]);
}

//error
export function extractError(e: any): string {
  if (e.message) {
    return e.message;
  } else if (e?.result?.errorMsg) {
    return e.result.errorMsg;
  } else if (e.errors) {
    let messages: unknown[];
    if (Array.isArray(e.errors)) {
      messages = e.errors;
    } else if (typeof e.errors === 'object') {
      messages = Object.values(e.errors).flatMap(e => Array.isArray(e) ? e : [e]);
    } else {
      messages = [e];
    }
    return messages.map(e => typeof e === 'string' ? e : JSON.stringify(e)).join('\n');
  } else if (e.title || e.detail) {
    return ((e.title ?? '') + '\n' + (e.detail ?? '')).trim();
  }
  return JSON.stringify(e);
}

export function handleError($toasted: Toasted, $i18n: VueI18n, e: any): void {
  if (ApiException.isApiException(e)) {
    let msg = e.message;
    if (msg.startsWith('t_')) {
      msg = $i18n.t(msg.substring(2)).toString();
    }
    if (e.status === 409) {
      msg = $i18n.t('error.conflict').toString();
    }
    $toasted.error('Error: ' + msg, { duration: 30000, action: { text: 'x', onClick: (_, t) => t.goAway(1) } });
  } else if (TokenExpiredException.isTokenExpiredException(e)) {
    window.location.href = e.redirectUrl;
  } else {
    let msg = extractError(e);
    if (msg.startsWith('t_')) {
      msg = $i18n.t(msg.substring(2)).toString();
    }
    if (e.status === 409 && e.title === 'Conflict') {
      msg = $i18n.t('error.conflict').toString();
    }
    $toasted.error('Error: ' + msg, { duration: 30000, action: { text: 'x', onClick: (_, t) => t.goAway(1) } });
  }
}

export class TokenExpiredException extends Error {
  readonly redirectUrl: string;
  readonly isTokenExpiredException = true;

  constructor(redirectUrl: string) {
    super();
    this.redirectUrl = redirectUrl;
  }

  static isTokenExpiredException(obj: any): obj is TokenExpiredException {
    return obj.isTokenExpiredException === true;
  }
}

export function tokenExpiredMiddleware(): ClientMiddleware {
  return {
    onResponse(res: Response): Response {
      if (res.redirected && res.url.includes('/manager/login')) {
        throw new TokenExpiredException(res.url);
      }
      return res;
    },
  };
}

export function apiKeyMiddleware(): ClientMiddleware {
  return {
    onRequest(url: RequestInfo, init: RequestInit): void {
      let windowUrl = new URL(window.location.href);
      if (windowUrl.searchParams.has('apiKey')) {
        init.headers ??= {};
        init.headers['X-Api-Key'] = windowUrl.searchParams.get('apiKey');
      }
    },
  };
}

export function createPatch<T>(...operations: Operation[]): T {
  return {
    ...operations,
    toJSON: function () {
      return operations;
    },
  } as any as T;
}

export type ExposedResponse<T> = { content: T, headers: Headers };
export type AnyFunction = (...a: any) => any;
export type ReplaceReturnType<T extends AnyFunction, TNewReturn> = (...a: Parameters<T>) => TNewReturn;
export type ReplaceProperty<T, KeyPropOld extends keyof T, TPropNew> = Omit<T, KeyPropOld> & { [K in KeyPropOld]: TPropNew };
export type ExposedClient<T extends { [K in keyof T]: AnyFunction }, F extends keyof T> =
  ReturnType<T[F]> extends Promise<any>
    ? ReplaceProperty<T, F, ReplaceReturnType<T[F], Promise<ExposedResponse<Awaited<ReturnType<T[F]>>>>>>
    : ReplaceProperty<T, F, ReplaceReturnType<T[F], ExposedResponse<ReturnType<T[F]>>>>;

/*
* Replaces the requested function in the client to expose the response header in the return value
**/
export function exposeHeaders<T extends { [K in keyof T]: AnyFunction }, F extends keyof T & string>(client: T, operation: F): ExposedClient<T, F> {
  const op = 'process' + operation[0].toUpperCase() + operation.substring(1);
  const original = (client as any)[op].bind(client);
  (client as any)[op] = async (response: Response) => ({ content: await original(response), headers: response.headers } as ExposedResponse<any>);
  return client as ExposedClient<T, F>;
}